/**
 * Shared, sandboxed expression evaluator.
 *
 * Used by both the server-side rule engine (computeOperations.js) and the
 * client-side field logic (headerFormula.js).
 *
 * Formula syntax:
 *   - Positional placeholders: $1, $2, … map to pre-resolved argument values
 *   - Supported math builtins: abs, min, max, round, floor, ceil, pow
 *   - Example: "$1 * (1 + $2)"  →  args[0] * (1 + args[1])
 *
 * Security:
 *   - Only a strict allowlist of identifiers is permitted inside the expression
 *   - Illegal characters (quotes, braces, semicolons, etc.) cause an error
 *   - Evaluation is done via `new Function` with no access to outer scope
 */

// Identifiers permitted inside a compiled expression.
// $N placeholders are rewritten to __args[N-1] before this check runs.
const ALLOWED_IDENTIFIERS = new Set([
    '__args', 'abs', 'min', 'max', 'round', 'floor', 'ceil', 'pow',
    'true', 'false', 'null'
])

const sanitize = (expr) => {
    const illegal = /[^0-9a-zA-Z_$\s\.\+\-*/%<>=!&|(),\[\]]/
    if (illegal.test(expr)) {
        throw new Error(`formula: illegal characters in expression: "${expr}"`)
    }
    const identifiers = expr.match(/[a-zA-Z_$][a-zA-Z0-9_$]*/g) || []
    for (const id of identifiers) {
        if (!ALLOWED_IDENTIFIERS.has(id)) {
            throw new Error(`formula: forbidden identifier: "${id}"`)
        }
    }
}

const compile = (formula) => {
    if (formula === 'true') {
        return () => true
    }
    if (formula === 'false') {
        return () => false
    }
    const expr = formula.replace(/\$(\d+)/g, (_, n) => `__args[${Number(n) - 1}]`)
    sanitize(expr)
    // eslint-disable-next-line no-new-func
    return new Function(
        '__args', 'abs', 'min', 'max', 'round', 'floor', 'ceil', 'pow',
        `return (${expr});`
    )
}

export const roundTo = (value, decimals = 5) => {
    const factor = 10 ** decimals
    return Math.round(value * factor) / factor
}

/**
 * Evaluate a formula with pre-resolved numeric arguments.
 *
 * @param {string}   formula       - Expression string with $1, $2, … placeholders
 * @param {number[]} resolvedArgs  - Already-resolved argument values
 * @param {object}   [options]
 * @param {number}   [options.decimals=5] - Rounding precision for the result
 * @returns {number|null} Result, or null if the expression does not produce a finite number
 */
export function evaluateExpression(formula, resolvedArgs, { decimals = 5 } = {}) {
    const fn = compile(formula)
    const result = fn(
        resolvedArgs,
        Math.abs, Math.min, Math.max, Math.round, Math.floor, Math.ceil, Math.pow
    )
    if (typeof result === 'boolean') return result
    if (typeof result === 'string') return result
    return Number.isFinite(result) ? roundTo(result, decimals) : null
}
